import React, { useState, ReactNode, useEffect } from "react";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import localStyles from "@presentation/components/notifications/Notifications.module.scss";
import { NotificationContext, NotificationType } from "@presentation/components/notifications/useNotifications";

interface NotificationProviderProps {
  children: ReactNode;
}

interface NotificationItem {
  message: string;
  type: NotificationType;
  id: number;
  duration?: number;
  durationStarted: boolean;
}

/**
 * Notifications provider component. Allows all children elements to access the notifications context and push notifications.
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [idCounter, setIdCounter] = useState(0);

  const pushNotification = (message: string, type: NotificationType, duration?: number) => {
    const id = idCounter;
    setNotifications([...notifications, { message, type, id, duration, durationStarted: false }]);
    setIdCounter(idCounter + 1);
  };

  const removeNotification = (id: number) => {
    setNotifications((prevNotifications) => prevNotifications.filter((notification) => notification.id !== id));
  };

  useEffect(() => {
    notifications.forEach((notification) => {
      if (notification.duration && !notification.durationStarted) {
        notification.durationStarted = true;
        setTimeout(() => {
          removeNotification(notification.id);
        }, notification.duration);
      }
    });
  }, [notifications]);

  return (
    <NotificationContext.Provider value={{ pushNotification }}>
      {children}
      <NotificationGroup className={localStyles.notification_group}>
        {notifications.map((notification) => (
          <Fade key={notification.id}>
            <Notification type={{ style: notification.type, icon: true }} closable={true} onClose={() => removeNotification(notification.id)}>
              <span>{notification.message}</span>
            </Notification>
          </Fade>
        ))}
      </NotificationGroup>
    </NotificationContext.Provider>
  );
};
